/**
 * InvestmentItemModal - Modal for creating and editing investment items
 *
 * Note: Uses a two-phase approach to avoid modal nesting issues.
 * When a picker is opened, the main dialog is hidden to prevent focus trap conflicts.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { Buildings, User, Tag, CalendarBlank, X, CurrencyEur, Cube, Spinner } from "@phosphor-icons/react";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Textarea } from "components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { UniversalPickerModal } from "shared/components/UniversalPickerModal";
import { useSession, useUser } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import { useUsers } from "shared/contexts/users.context";
import { getUserName } from "shared/utils/users";
import {
  InvestmentItem,
  InvestmentItemAuditLog,
  RenovationLabel,
} from "../types/investment";
import { UnitDTO } from "shared/types/unit";
import { AuditTrailSection } from "./AuditTrailSection";

interface InvestmentItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editingItem?: InvestmentItem | null;
  /** Pre-select a building when opening the modal (e.g., when creating from building context) */
  defaultBuildingId?: string;
}

type PickerType = "building" | "assignee" | null;

// Searchable Label Dropdown Component
interface LabelDropdownProps {
  selectedLabel: RenovationLabel | null;
  availableLabels: RenovationLabel[];
  onLabelSelect: (label: RenovationLabel | null) => void;
  isDisabled?: boolean;
}

const LabelDropdown: React.FC<LabelDropdownProps> = ({
  selectedLabel,
  availableLabels,
  onLabelSelect,
  isDisabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filteredLabels = availableLabels.filter(
    (label) =>
      label.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      label.type.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isOpen]);

  const handleSelect = (label: RenovationLabel | null) => {
    onLabelSelect(label);
    setIsOpen(false);
    setSearchTerm("");
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !isDisabled && setIsOpen(!isOpen)}
        disabled={isDisabled}
        className={`flex min-h-[40px] w-full items-center justify-between gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors ${
          isDisabled
            ? "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400"
            : "border-input bg-background hover:bg-accent hover:text-accent-foreground"
        }`}
      >
        <div className="flex items-center gap-2">
          <Tag className="text-brick-primary h-4 w-4" />
          {selectedLabel ? (
            <span className="truncate">
              {selectedLabel.name} ({selectedLabel.type})
            </span>
          ) : (
            <span className="text-muted-foreground">Selecteer label...</span>
          )}
        </div>
      </button>

      {isOpen && !isDisabled && (
        <div className="bg-popover absolute top-full right-0 left-0 z-50 mt-1 max-h-64 overflow-hidden rounded-md border shadow-md">
          <div className="border-b p-2">
            <input
              ref={inputRef}
              type="text"
              placeholder="Zoek label..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="focus:ring-ring w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:outline-none"
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            <button
              type="button"
              onClick={() => handleSelect(null)}
              className="text-muted-foreground hover:bg-accent w-full px-3 py-2 text-left text-sm"
            >
              Geen label
            </button>
            {filteredLabels.map((label) => (
              <button
                key={label.id}
                type="button"
                onClick={() => handleSelect(label)}
                className="hover:bg-accent w-full px-3 py-2 text-left text-sm"
              >
                <span className="font-medium">{label.name}</span>
                <span className="text-muted-foreground ml-2">
                  ({label.type})
                </span>
              </button>
            ))}
            {filteredLabels.length === 0 && (
              <div className="text-muted-foreground px-3 py-2 text-sm">
                Geen labels gevonden
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export function InvestmentItemModal({
  isOpen,
  onClose,
  onSuccess,
  editingItem,
  defaultBuildingId,
}: InvestmentItemModalProps) {
  const { session } = useSession();
  const { user } = useUser();
  const { users } = useUsers();
  const isEditing = !!editingItem;

  // Track if we've initialized the form for this modal open
  const [formInitialized, setFormInitialized] = useState(false);

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [estimatedCost, setEstimatedCost] = useState("");
  const [selectedBuilding, setSelectedBuilding] = useState<any>(null);
  const [responsibleUserId, setResponsibleUserId] = useState<string>("");
  const [selectedLabel, setSelectedLabel] = useState<RenovationLabel | null>(
    null,
  );
  const [estimatedStartDate, setEstimatedStartDate] = useState<string>("");
  const [estimatedDeadline, setEstimatedDeadline] = useState<string>("");
  const [creationReason, setCreationReason] = useState<string>("");
  const [budgetChangeReason, setBudgetChangeReason] = useState<string>("");

  // Track original budget for edit mode to detect changes
  const [originalBudget, setOriginalBudget] = useState<number | null>(null);

  // Available labels
  const [availableLabels, setAvailableLabels] = useState<RenovationLabel[]>([]);

  // Units (kostenplaatsen) state
  const [selectedUnitIds, setSelectedUnitIds] = useState<string[]>([]);
  const [buildingUnits, setBuildingUnits] = useState<UnitDTO[]>([]);
  const [loadingUnits, setLoadingUnits] = useState(false);

  // Track which picker is open - when a picker is open, main dialog is hidden
  const [activePicker, setActivePicker] = useState<PickerType>(null);

  // Loading state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Audit trail (only meaningful in edit mode) — fetched fresh each time the
  // modal opens for an existing item so users see the latest history.
  const [auditLogs, setAuditLogs] = useState<InvestmentItemAuditLog[]>([]);
  const [isLoadingAudit, setIsLoadingAudit] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!isOpen || !editingItem?.id || !session) {
      setAuditLogs([]);
      return;
    }
    setIsLoadingAudit(true);
    (async () => {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/investment/items/${editingItem.id}`,
          { method: "GET" },
          session,
        );
        if (!response.ok) throw new Error("Failed to fetch audit trail");
        const data = await response.json();
        if (!cancelled) {
          setAuditLogs(data?.auditLogs ?? []);
        }
      } catch (error) {
        console.error("Error fetching investment audit trail:", error);
        if (!cancelled) setAuditLogs([]);
      } finally {
        if (!cancelled) setIsLoadingAudit(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, editingItem?.id, session]);

  // Fetch available renovation labels
  useEffect(() => {
    const fetchLabels = async () => {
      if (!session) return;
      try {
        const response = await fetchWithAuth(
          `${API_URL}/renovation/labels`,
          { method: "GET" },
          session,
        );
        if (response.ok) {
          const data = await response.json();
          setAvailableLabels(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error("Error fetching renovation labels:", error);
      }
    };
    fetchLabels();
  }, [session]);

  // Fetch building data when defaultBuildingId is provided
  useEffect(() => {
    const fetchBuilding = async () => {
      if (!session || !defaultBuildingId || editingItem) {
        // Don't fetch if editing (editingItem already has building) or no defaultBuildingId
        return;
      }
      try {
        const response = await fetchWithAuth(
          `${API_URL}/building/${defaultBuildingId}`,
          { method: "GET" },
          session,
        );
        if (response.ok) {
          const building = await response.json();
          setSelectedBuilding(building);
        }
      } catch (error) {
        console.error("Error fetching building:", error);
      }
    };

    // Only fetch when modal opens and we have a defaultBuildingId
    if (isOpen && defaultBuildingId && !editingItem && !selectedBuilding) {
      fetchBuilding();
    }
  }, [session, defaultBuildingId, isOpen, editingItem, selectedBuilding]);

  // Fetch units when building is selected
  useEffect(() => {
    const fetchUnits = async () => {
      if (!session || !selectedBuilding?.id) {
        setBuildingUnits([]);
        return;
      }

      setLoadingUnits(true);
      try {
        const response = await fetchWithAuth(
          `${API_URL}/units/building/${selectedBuilding.id}`,
          { method: "GET" },
          session,
        );
        if (response.ok) {
          const data = await response.json();
          setBuildingUnits(Array.isArray(data) ? data : data.data || []);
        }
      } catch (error) {
        console.error("Error fetching building units:", error);
        setBuildingUnits([]);
      } finally {
        setLoadingUnits(false);
      }
    };
    fetchUnits();
  }, [session, selectedBuilding?.id]);

  // Initialize form ONLY when modal freshly opens (not when returning from picker)
  useEffect(() => {
    if (isOpen && !formInitialized) {
      if (editingItem) {
        setTitle(editingItem.title || "");
        setDescription(editingItem.description || "");
        setEstimatedCost(editingItem.estimatedCostExclVat?.toString() || "");
        setSelectedBuilding(editingItem.building || null);
        setResponsibleUserId(editingItem.responsibleUserId || "");
        setSelectedLabel(editingItem.label || null);
        // Handle date fields - convert from ISO to date input format
        setEstimatedStartDate(
          editingItem.estimatedStartDate
            ? new Date(editingItem.estimatedStartDate)
                .toISOString()
                .split("T")[0]
            : "",
        );
        setEstimatedDeadline(
          editingItem.estimatedDeadline
            ? new Date(editingItem.estimatedDeadline)
                .toISOString()
                .split("T")[0]
            : "",
        );
        // Initialize units from existing item
        setSelectedUnitIds(
          editingItem.units?.map((u) => u.id).filter(Boolean) as string[] || []
        );
        // Track original budget for change detection
        setOriginalBudget(editingItem.estimatedCostExclVat || 0);
        setBudgetChangeReason("");
      } else {
        // Reset to defaults for new item
        setTitle("");
        setDescription("");
        setEstimatedCost("");
        setSelectedBuilding(null);
        setResponsibleUserId(user?.id || "");
        setSelectedLabel(null);
        setEstimatedStartDate("");
        setEstimatedDeadline("");
        setCreationReason("");
        setSelectedUnitIds([]);
        setOriginalBudget(null);
        setBudgetChangeReason("");
      }
      setFormInitialized(true);
    }
  }, [isOpen, formInitialized, editingItem, user]);

  // Reset formInitialized when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormInitialized(false);
      setActivePicker(null);
    }
  }, [isOpen]);

  // Detect if budget has changed during editing
  const newBudgetValue = parseFloat(estimatedCost) || 0;
  const hasBudgetChanged =
    isEditing && originalBudget !== null && newBudgetValue !== originalBudget;

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    // Validation
    if (!title.trim()) {
      toast.error("Titel is verplicht");
      return;
    }
    if (!selectedBuilding) {
      toast.error("Gebouw is verplicht");
      return;
    }
    if (!estimatedCost || isNaN(parseFloat(estimatedCost))) {
      toast.error("Geschatte kosten is verplicht");
      return;
    }

    // Require creation reason for new items
    if (!isEditing && !creationReason.trim()) {
      toast.error("Reden voor aanmaak is verplicht");
      return;
    }

    // Require budget change reason when editing and budget changed
    const budgetValue = parseFloat(estimatedCost) || 0;
    const budgetChanged =
      isEditing && originalBudget !== null && budgetValue !== originalBudget;

    if (budgetChanged && (!budgetChangeReason.trim() || budgetChangeReason.trim().length < 5)) {
      toast.error("Reden voor budgetwijziging is verplicht (minimaal 5 karakters)");
      return;
    }

    setIsSubmitting(true);

    try {
      const basePayload = {
        title: title.trim(),
        description: description.trim(),
        buildingId: selectedBuilding.id,
        responsibleUserId: responsibleUserId || user?.id || "",
        labelId: selectedLabel?.id || null,
        estimatedStartDate: estimatedStartDate || null,
        estimatedDeadline: estimatedDeadline || null,
        unitIds: selectedUnitIds.length > 0 ? selectedUnitIds : undefined,
      };

      if (isEditing && editingItem) {
        // Update existing item (without budget if it changed)
        const updatePayload = budgetChanged
          ? basePayload
          : { ...basePayload, estimatedCostExclVat: budgetValue };

        const response = await fetchWithAuth(
          `${API_URL}/investment/items/${editingItem.id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(updatePayload),
          },
          session,
        );

        if (!response.ok) {
          throw new Error("Failed to update item");
        }

        // If budget changed, call the change-budget endpoint to create audit log
        if (budgetChanged) {
          const budgetResponse = await fetchWithAuth(
            `${API_URL}/investment/items/${editingItem.id}/change-budget`,
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                newBudget: budgetValue,
                reason: budgetChangeReason.trim(),
              }),
            },
            session,
          );

          if (!budgetResponse.ok) {
            const error = await budgetResponse.json().catch(() => ({}));
            throw new Error(error.message || "Failed to change budget");
          }
        }

        toast.success("Investeringslijn bijgewerkt");
      } else {
        // Create new item - include creation reason, budget, and createdByUserId
        const createPayload = {
          ...basePayload,
          estimatedCostExclVat: budgetValue,
          creationReason: creationReason.trim(),
          createdByUserId: user?.id || "",
        };
        const response = await fetchWithAuth(
          `${API_URL}/investment/items`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(createPayload),
          },
          session,
        );

        if (!response.ok) {
          throw new Error("Failed to create item");
        }

        toast.success("Investeringslijn aangemaakt");
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error saving investment item:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : isEditing
            ? "Fout bij het bijwerken van de investeringslijn"
            : "Fout bij het aanmaken van de investeringslijn",
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    title,
    description,
    selectedBuilding,
    estimatedCost,
    responsibleUserId,
    selectedLabel,
    estimatedStartDate,
    estimatedDeadline,
    selectedUnitIds,
    creationReason,
    budgetChangeReason,
    originalBudget,
    isEditing,
    editingItem,
    session,
    user,
    onSuccess,
    onClose,
  ]);

  // Handle building selection
  const handleBuildingSelect = useCallback((building: any) => {
    setSelectedBuilding(building);
    // Clear selected units when building changes (they belong to a different building)
    setSelectedUnitIds([]);
    setActivePicker(null); // Close picker and reopen main dialog
  }, []);

  // Handle assignee selection
  const handleAssigneeSelect = useCallback((assignee: any) => {
    setResponsibleUserId(assignee?.id || "");
    setActivePicker(null); // Close picker and reopen main dialog
  }, []);

  // Get responsible user name
  const getResponsibleUserName = () => {
    if (!responsibleUserId) return null;
    const userData = getUserName(responsibleUserId, users);
    return userData?.name || "Gebruiker geselecteerd";
  };

  // Main dialog should only show when no picker is active
  const showMainDialog = isOpen && activePicker === null;

  return (
    <>
      {/* Main Dialog */}
      <Dialog open={showMainDialog} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {isEditing
                ? "Investeringslijn bewerken"
                : "Nieuwe investeringslijn"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="title">Titel *</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Bijv. Dakisolatie gebouw A"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Beschrijving</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Beschrijf de investering..."
                rows={3}
              />
            </div>

            {/* Building */}
            <div className="space-y-2">
              <Label>Gebouw *</Label>
              <Button
                variant="outline"
                onClick={() => setActivePicker("building")}
                className="w-full justify-start"
                type="button"
              >
                <Buildings className="text-moss-primary mr-2 h-4 w-4" />
                {selectedBuilding ? (
                  <div className="flex flex-col items-start">
                    <span className="font-medium">{selectedBuilding.id}</span>
                    {selectedBuilding.company?.name && (
                      <span className="text-muted-foreground text-xs">
                        {selectedBuilding.company.name}
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-muted-foreground">
                    Selecteer gebouw...
                  </span>
                )}
              </Button>
            </div>

            {/* Kostenplaatsen (Units) - only show when building is selected */}
            {selectedBuilding && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Kostenplaatsen</Label>
                  {!loadingUnits && buildingUnits.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const allUnitIds = buildingUnits
                            .filter((u) => u.id)
                            .map((u) => u.id!);
                          setSelectedUnitIds(allUnitIds);
                        }}
                        disabled={isSubmitting}
                        className="text-xs h-6 px-2"
                      >
                        Alles
                      </Button>
                      {selectedUnitIds.length > 0 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedUnitIds([])}
                          disabled={isSubmitting}
                          className="text-xs h-6 px-2 text-red-600"
                        >
                          Wissen
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {loadingUnits ? (
                  <div className="flex items-center gap-2 text-gray-500 text-sm py-2">
                    <Spinner className="h-4 w-4 animate-spin" />
                    Units laden...
                  </div>
                ) : buildingUnits.length === 0 ? (
                  <div className="text-sm text-gray-400 italic py-2">
                    Geen units gevonden voor dit gebouw
                  </div>
                ) : (
                  <>
                    <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto p-2 border rounded-md bg-gray-50">
                      {buildingUnits
                        .filter((u) => u.id)
                        .map((unit) => {
                          const isSelected = selectedUnitIds.includes(unit.id!);
                          return (
                            <button
                              key={unit.id}
                              type="button"
                              onClick={() => {
                                if (isSelected) {
                                  setSelectedUnitIds((prev) =>
                                    prev.filter((id) => id !== unit.id)
                                  );
                                } else {
                                  setSelectedUnitIds((prev) => [...prev, unit.id!]);
                                }
                              }}
                              disabled={isSubmitting}
                              className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border transition-colors ${
                                isSelected
                                  ? "bg-moss-primary text-white border-moss-primary"
                                  : "bg-white text-gray-600 border-gray-200 hover:border-moss-primary hover:bg-moss-primary/10"
                              }`}
                            >
                              <Cube className="h-3 w-3" />
                              {unit.id}
                            </button>
                          );
                        })}
                    </div>
                    {selectedUnitIds.length > 0 && (
                      <p className="text-xs text-gray-500">
                        {selectedUnitIds.length} kostenplaats(en) geselecteerd
                      </p>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Renovation Label - Searchable */}
            <div className="space-y-2">
              <Label>Renovatielabel</Label>
              <LabelDropdown
                selectedLabel={selectedLabel}
                availableLabels={availableLabels}
                onLabelSelect={setSelectedLabel}
                isDisabled={isSubmitting}
              />
            </div>

            {/* Estimated Cost */}
            <div className="space-y-2">
              <Label htmlFor="estimatedCost">
                Geschatte kosten (excl. BTW) *
              </Label>
              <div className="relative">
                <span className="text-muted-foreground absolute top-1/2 left-3 -translate-y-1/2">
                  €
                </span>
                <Input
                  id="estimatedCost"
                  type="number"
                  value={estimatedCost}
                  onChange={(e) => setEstimatedCost(e.target.value)}
                  placeholder="0.00"
                  className="pl-7"
                  step="0.01"
                  min="0"
                />
              </div>
            </div>

            {/* Date Fields Row */}
            <div className="grid grid-cols-2 gap-4">
              {/* Estimated Start Date */}
              <div className="space-y-2">
                <Label htmlFor="estimatedStartDate">Geschatte startdatum</Label>
                <div className="relative">
                  <CalendarBlank className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                  <Input
                    id="estimatedStartDate"
                    type="date"
                    value={estimatedStartDate}
                    onChange={(e) => setEstimatedStartDate(e.target.value)}
                    className={`pl-10 ${estimatedStartDate ? "pr-8" : ""}`}
                  />
                  {estimatedStartDate && (
                    <button
                      type="button"
                      onClick={() => setEstimatedStartDate("")}
                      className="absolute top-1/2 right-2 -translate-y-1/2 rounded p-1 hover:bg-gray-100"
                      title="Datum wissen"
                    >
                      <X className="text-muted-foreground h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>

              {/* Estimated Deadline */}
              <div className="space-y-2">
                <Label htmlFor="estimatedDeadline">Geschatte deadline</Label>
                <div className="relative">
                  <CalendarBlank className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                  <Input
                    id="estimatedDeadline"
                    type="date"
                    value={estimatedDeadline}
                    onChange={(e) => setEstimatedDeadline(e.target.value)}
                    className={`pl-10 ${estimatedDeadline ? "pr-8" : ""}`}
                  />
                  {estimatedDeadline && (
                    <button
                      type="button"
                      onClick={() => setEstimatedDeadline("")}
                      className="absolute top-1/2 right-2 -translate-y-1/2 rounded p-1 hover:bg-gray-100"
                      title="Datum wissen"
                    >
                      <X className="text-muted-foreground h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Responsible User */}
            <div className="space-y-2">
              <Label>Verantwoordelijke</Label>
              <Button
                variant="outline"
                onClick={() => setActivePicker("assignee")}
                className="w-full justify-start"
                type="button"
              >
                <User className="mr-2 h-4 w-4 text-blue-500" />
                {responsibleUserId ? (
                  <span>{getResponsibleUserName()}</span>
                ) : (
                  <span className="text-muted-foreground">
                    Selecteer verantwoordelijke...
                  </span>
                )}
              </Button>
            </div>

            {/* Budget Change Reason - only when editing and budget changed */}
            {hasBudgetChanged && (
              <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-4">
                <div className="flex items-center gap-2 text-amber-700 font-medium">
                  <CurrencyEur className="h-4 w-4" />
                  Budget gewijzigd
                </div>
                <div className="text-sm text-amber-600">
                  Huidig: €{originalBudget?.toLocaleString("nl-BE")} → Nieuw: €
                  {newBudgetValue.toLocaleString("nl-BE")}
                </div>
                <Label
                  htmlFor="budgetChangeReason"
                  className="text-amber-700 font-medium"
                >
                  Reden voor wijziging *
                </Label>
                <Textarea
                  id="budgetChangeReason"
                  value={budgetChangeReason}
                  onChange={(e) => setBudgetChangeReason(e.target.value)}
                  placeholder="Waarom wordt het budget gewijzigd? Vb. Herziene offerte, scopewijziging, ..."
                  rows={3}
                  className="bg-white"
                />
                <p className="text-xs text-amber-600">
                  Minimaal 5 karakters. Deze informatie wordt opgeslagen in de
                  audit trail.
                </p>
              </div>
            )}

            {/* Creation Reason - only for new items */}
            {!isEditing && (
              <div className="border-moss-primary/30 bg-moss-primary/5 space-y-2 rounded-lg border p-4">
                <Label
                  htmlFor="creationReason"
                  className="text-moss-primary font-medium"
                >
                  Reden voor aanmaak *
                </Label>
                <Textarea
                  id="creationReason"
                  value={creationReason}
                  onChange={(e) => setCreationReason(e.target.value)}
                  placeholder="Waarom wordt deze investeringslijn aangemaakt? Vb. Onderdeel van het acquisitiebudget, ..."
                  rows={3}
                  className="bg-white"
                />
                <p className="text-muted-foreground text-xs">
                  Deze reden wordt vastgelegd in de audit trail van dit item.
                </p>
              </div>
            )}

            {/* Audit Trail — only when editing an existing item */}
            {isEditing && (
              <div className="border-t pt-4">
                <AuditTrailSection
                  auditLogs={auditLogs}
                  isLoading={isLoadingAudit}
                />
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 border-t pt-4">
            <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
              Annuleren
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? "Bezig..." : isEditing ? "Opslaan" : "Aanmaken"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Building Picker - rendered when active */}
      {activePicker === "building" && (
        <UniversalPickerModal
          isOpen={true}
          onClose={() => setActivePicker(null)}
          mode="building"
          title="Selecteer gebouw"
          onConfirm={handleBuildingSelect}
        />
      )}

      {/* Assignee Picker - rendered when active */}
      {activePicker === "assignee" && (
        <UniversalPickerModal
          isOpen={true}
          onClose={() => setActivePicker(null)}
          mode="assignee"
          title="Selecteer verantwoordelijke"
          onConfirm={handleAssigneeSelect}
        />
      )}
    </>
  );
}
